import { keepPreviousData } from '@tanstack/react-query';
import {
    PortfolioProductOverviewEntriesFilters,
    PortfolioProductOverviewEntry,
    useInvalidateRepositoryQuery,
    useRepositoryMutation,
    useRepositoryQuery,
} from '@mews-commander/data-access';
import { Link, TableState } from '@optimus-web/core';
import { lt, showErrorAlert, showSuccessAlert } from '@mews/framework';
import { ArticlePath, createGuideLink } from '@mews-commander/enums';

export const useGetLocalProductOverviewEntries = (
    tableState: TableState<PortfolioProductOverviewEntry, PortfolioProductOverviewEntriesFilters>
) =>
    useRepositoryQuery('portfolioProducts', 'getOverviewEntries', tableState, {
        placeholderData: keepPreviousData,
    });

export const useGetLocalProductDetail = (enterpriseProductId: string) =>
    useRepositoryQuery('portfolioEnterpriseProducts', 'getDetail', { enterpriseProductId });

export const useDeleteLocalProduct = () => {
    const guideLink = createGuideLink(ArticlePath.HowToDeleteProductsInMewsMultiProperty);

    return useRepositoryMutation('portfolioEnterpriseProducts', 'delete', {
        invalidateQueries: {
            portfolioEnterpriseProducts: ['getConfigurationEntries'],
            portfolioCentralProducts: ['getOverviewEntries', 'getDetail'],
        },
        onSuccess: () =>
            showSuccessAlert({
                description: lt('LocalProductDeletedDescription'),
            }),
        onError: () =>
            showErrorAlert({
                description: lt('LocalProductDeletedDescriptionError'),
                action: {
                    onClick: () => undefined,
                    text: (
                        <Link variant="basic" href={guideLink} target="_blank" rel="noreferrer">
                            {lt('LearnMore')}
                        </Link>
                    ),
                },
            }),
    });
};

export const useDeleteServiceProduct = (enterpriseProductId?: string, onSuccess?: () => void) => {
    const invalidateQuery = useInvalidateRepositoryQuery();

    return useRepositoryMutation('portfolioProducts', 'delete', {
        invalidateQueries: { portfolioProducts: ['getOverviewEntries'] },
        onSuccess: async () => {
            if (enterpriseProductId) {
                await invalidateQuery('portfolioEnterpriseProducts', 'getDetail', {
                    enterpriseProductId,
                });
            }
            showSuccessAlert({
                description: lt('ServiceProductDeletedDescription'),
            });
            onSuccess?.();
        },
    });
};
